import {
  ChangeEvent,
  Dispatch,
  FC,
  FormEvent,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import { Trans, useTranslation } from 'next-i18next'

import {
  PrimaryButton,
  SecondaryButton,
  LoadingSpinner,
  Input,
  Checkbox,
} from 'ui'

import {
  PasswordValidationBox,
  PasswordValidationMessage,
} from '../PasswordValidationBox'
import { AuthFooter } from '../AuthLayout'
import MarketPreferenceSelect from './MarketPreferenceSelect'

import useAuth from '../../hooks/useAuth'
import {
  MARKET_PREFERENCE,
  USER_TYPE,
} from '../../services/context/AuthContext'
import { useTracking } from '../../services/context/TrackingContext'

import { validatePassword } from '../../utils/validations'
import { onPhoneNumberChanged } from '../../utils/onPhoneNumberChanged'

import { GoogleButtonIcon } from 'icons'

const SignUpForm: FC<{
  isLoading: boolean
  email: string
  setEmail: Dispatch<SetStateAction<string>>
  password: string
  setPassword: Dispatch<SetStateAction<string>>
  back: () => void
}> = ({ isLoading, email, setEmail, password, setPassword, back }) => {
  const { t } = useTranslation(['auth', 'common'])
  const { track } = useTracking()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [allowSms, setAllowSms] = useState(false)
  const [userType, setUserType] = useState(USER_TYPE.CUSTOMER)
  const [marketPreference, setMarketPreference] = useState<
    MARKET_PREFERENCE | undefined
  >(undefined)

  const [passwordValidation, setPasswordValidation] = useState({
    hasLowercase: false,
    hasUppercase: false,
    hasSpecialCharacter: false,
    hasNumeric: false,
    hasValidLength: false,
    isValid: false,
  })

  const { signUp, errorMessage, unverifiedAccount, federatedSignIn } = useAuth()

  useEffect(() => {
    if (unverifiedAccount.email.length > 0) {
      track({
        event: 'Sign Up Flow Submitted',
        properties: {
          form_name: 'Account Creation',
          form_step_count: 2,
          form_step_id: 1,
          form_step_name: 'Account Creation',
          form_field_count: 1,
          form_fields: [
            {
              field_type: 'Multiple Paths',
              field_name: 'Account Creation Method',
              field_value: 'Manual',
              field_required: true,
            },
            {
              field_type: 'Text Input',
              field_name: 'First Name',
              field_value: firstName,
              field_required: true,
            },
            {
              field_type: 'Text Input',
              field_name: 'Last Name',
              field_value: lastName,
              field_required: true,
            },
            {
              field_type: 'Email Address',
              field_name: 'Email',
              field_value: email,
              field_required: true,
            },
            {
              field_type: 'Checkbox',
              field_name: 'Are you a real state agent?',
              field_value: userType === USER_TYPE.AGENT,
              field_required: false,
            },
            {
              field_type: 'Phone Number',
              field_name: 'Phone Number',
              field_value: phoneNumber,
              field_required: false,
            },
            {
              field_type: 'Checkbox',
              field_name: 'SMS consent',
              field_value: allowSms,
              field_required: false,
            },
            {
              field_type: 'Dropdown',
              field_name: 'Where are you planning to buy a home?',
              field_value: marketPreference,
              field_required: true,
            },
          ],
        },
      })
    }
  }, [
    unverifiedAccount,
    email,
    track,
    firstName,
    lastName,
    phoneNumber,
    allowSms,
    marketPreference,
    userType,
  ])

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <>
      <button
        className="flex w-full items-center justify-center rounded-[4px] border-2 border-[#346EF1] px-20 py-[7px] text-[#346EF1] util-label"
        onClick={() => {
          federatedSignIn({
            provider: 'Google',
          })
        }}
      >
        <GoogleButtonIcon className="rect-18" aria-hidden="true" />
        <span className="ml-24 block">
          {t('early-access.sign-up-with-google')}
        </span>
      </button>

      <div className="relative mt-24 border-t border-green-1">
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-24 text-grey-6 util-label">
          {t('or')}
        </span>
      </div>

      <form
        className="mt-24 space-y-16"
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault()
          signUp({
            email,
            password,
            firstName,
            lastName,
            phoneNumber,
            marketPreference: marketPreference ?? MARKET_PREFERENCE.NO_ANSWER,
            allowSms: allowSms,
            userType,
          })
        }}
      >
        <Input
          required
          id="first_name"
          label={t('sign-up-form.first-name')}
          placeholder={t('sign-up-form.first-name')}
          value={firstName}
          onChange={(e) => setFirstName(e.currentTarget.value)}
          error={!firstName && !!errorMessage.signup}
        />
        <Input
          required
          id="last_name"
          label={t('sign-up-form.last-name')}
          placeholder={t('sign-up-form.last-name')}
          value={lastName}
          onChange={(e) => setLastName(e.currentTarget.value)}
          error={!lastName && !!errorMessage.signup}
        />
        <Input
          required
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          label={t('email')}
          id="email"
          type="email"
          placeholder={t('email')}
          error={!!errorMessage.signup}
          helperText={errorMessage.signup}
          autoComplete="email"
        />
        <Input
          required
          value={password}
          onChange={(e) => {
            const pw = e.currentTarget.value
            const passwordValidationObj = validatePassword(pw)

            setPasswordValidation(passwordValidationObj)
            setPassword(pw)
          }}
          label={t('password')}
          autoComplete="new-password"
          id="new-password"
          placeholder={t('password')}
          type="password"
          error={!!errorMessage.signup}
        />

        {password ? (
          <PasswordValidationBox className="mt-24">
            <PasswordValidationMessage
              isValid={passwordValidation.hasLowercase}
              msg={t('password-validation.lowercase')}
            />
            <PasswordValidationMessage
              isValid={passwordValidation.hasUppercase}
              msg={t('password-validation.uppercase')}
            />
            <PasswordValidationMessage
              isValid={passwordValidation.hasSpecialCharacter}
              msg={t('password-validation.special')}
            />
            <PasswordValidationMessage
              isValid={passwordValidation.hasNumeric}
              msg={t('password-validation.numeric')}
            />
            <PasswordValidationMessage
              isValid={passwordValidation.hasValidLength}
              msg={t('password-validation.legth')}
            />
          </PasswordValidationBox>
        ) : null}

        <MarketPreferenceSelect
          value={marketPreference}
          onChange={(e) => {
            setMarketPreference(e.currentTarget.value as MARKET_PREFERENCE)
          }}
        />

        <div className="pt-8">
          <div>
            <Checkbox
              id="are-you-a-real-state-agent"
              labelRegular
              label={t('sign-up-form.are-you-a-real-state-agent')}
              checked={userType === USER_TYPE.AGENT}
              customOnChange={(id: string, value: boolean) => {
                setUserType(value ? USER_TYPE.AGENT : USER_TYPE.CUSTOMER)
              }}
            />
          </div>
        </div>

        <Input
          id="phone_number"
          label={t('sign-up-form.phone-number')}
          placeholder={t('sign-up-form.phone-number')}
          value={phoneNumber}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const getPhone = onPhoneNumberChanged(e.currentTarget.value)
            if (getPhone.isValid) {
              setPhoneNumber(getPhone.phone)
            }
          }}
        />

        <div className="pt-8">
          <div>
            <Checkbox
              disabled={!phoneNumber}
              id="allow-sms"
              labelRegular
              label={t('sign-up-form.allow-sms')}
              checked={allowSms}
              customOnChange={(id: string, value: boolean) => {
                setAllowSms(value)
              }}
            />
          </div>
          <p className="ml-[26px] mt-4 text-10 leading-snug text-grey-8">
            <Trans t={t} i18nKey="sign-up-form.allow-sms-description">
              By submitting this form and signing up via text, you consent to
              receive marketing text messages (such as new listings and
              reminders) from Acre at the number provided, including messages
              sent by autodialer. Consent is not a condition of any purchase.
              Message and data rates may apply. Message frequency varies. You
              can unsubscribe at any time by replying STOP or clicking the
              unsubscribe link (where available) in one of our messages. View
              our{' '}
              <a
                target="_blank"
                rel="noreferrer"
                className="text-teal-4 underline"
                href="/privacy-policy"
              >
                Privacy Policy
              </a>{' '}
              .
            </Trans>
          </p>
        </div>

        <AuthFooter>
          <div className="flex">
            <SecondaryButton onClick={back} className="hidden md:block">
              {t('ui.back', { ns: 'common' })}
            </SecondaryButton>
            <PrimaryButton
              disabled={
                !firstName ||
                !lastName ||
                !email ||
                !passwordValidation.isValid ||
                !marketPreference
              }
              type="submit"
              className="mx-auto block w-full max-w-420 md:mr-0 md:w-auto"
            >
              {t('early-access.create-account')}
            </PrimaryButton>
          </div>
        </AuthFooter>
      </form>
    </>
  )
}

export default SignUpForm
